import type {
  GraphRecords,
  MaterialNodeSpec,
  OperationNodeSpec,
  TransferRow,
  Unit,
  Quantity,
} from '@/graph/domain/types';
import {
  computeSourcePostQuantity,
  computeMergedQuantity,
  mergeConstituents,
  convertToUnit,
  transferRowToFormState,
} from '@/graph-store/actions/transferHelpers';

export interface MergeContext {
  deletedInputMaterialId: string;
  deletedInputQuantity: number;
  removedContributionsByOutput?: Map<string, number>;
}

export interface PropagateOptions {
  skipIds?: Set<string>;
  mergeContext?: MergeContext;
  fields?: {
    identity?: boolean;
    quantity?: boolean;
    constituents?: boolean;
  };
  oldGraphData?: GraphRecords;  // For intelligent quantity recalculation when input changes
}

interface PropagateFlags {
  identity: boolean;
  quantity: boolean;
  constituents: boolean;
}

function normalizeFlags(fields?: PropagateOptions['fields']): PropagateFlags {
  return {
    identity: fields?.identity ?? true,
    quantity: fields?.quantity ?? true,
    constituents: fields?.constituents ?? true,
  };
}

function toFiniteNumber(value: unknown, fallback = 0): number {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function resolveYieldFactor(op: OperationNodeSpec): number {
  const explicit = op.yield;
  if (explicit && Number.isFinite(explicit.amount)) {
    const amount = Number(explicit.amount);
    return explicit.isPercent ? amount / 100 : amount;
  }

  const paramYield = Number(op.parameters?.yield);
  if (Number.isFinite(paramYield)) {
    return paramYield;
  }

  return 1;
}

function buildAdjacency(graphData: GraphRecords): {
  matToOps: Map<string, Set<string>>;
  opToMats: Map<string, Set<string>>;
  opToInputs: Map<string, Set<string>>;
} {
  const matToOps = new Map<string, Set<string>>();
  const opToMats = new Map<string, Set<string>>();
  const opToInputs = new Map<string, Set<string>>();

  for (const edge of Object.values(graphData.flowEdges)) {
    if (edge.edgeKind === 'graphsLink') continue;

    if (graphData.materialNodes[edge.sourceId] && graphData.operationNodes[edge.targetId]) {
      const set = matToOps.get(edge.sourceId) ?? new Set<string>();
      set.add(edge.targetId);
      matToOps.set(edge.sourceId, set);

      const inputs = opToInputs.get(edge.targetId) ?? new Set<string>();
      inputs.add(edge.sourceId);
      opToInputs.set(edge.targetId, inputs);
    }

    if (graphData.operationNodes[edge.sourceId] && graphData.materialNodes[edge.targetId]) {
      const set = opToMats.get(edge.sourceId) ?? new Set<string>();
      set.add(edge.targetId);
      opToMats.set(edge.sourceId, set);
    }
  }

  return { matToOps, opToMats, opToInputs };
}

function collectReachable(
  graphData: GraphRecords,
  startMaterialId: string,
  matToOps: Map<string, Set<string>>,
  opToMats: Map<string, Set<string>>,
): { reachableMaterials: Set<string>; reachableOps: Set<string> } {
  const reachableMaterials = new Set<string>([startMaterialId]);
  const reachableOps = new Set<string>();
  const queue: Array<{ kind: 'mat' | 'op'; id: string }> = [{ kind: 'mat', id: startMaterialId }];

  while (queue.length > 0) {
    const node = queue.shift()!;

    if (node.kind === 'mat') {
      for (const opId of matToOps.get(node.id) ?? []) {
        if (reachableOps.has(opId)) continue;
        reachableOps.add(opId);
        queue.push({ kind: 'op', id: opId });
      }
      continue;
    }

    for (const outMatId of opToMats.get(node.id) ?? []) {
      if (reachableMaterials.has(outMatId)) continue;
      reachableMaterials.add(outMatId);
      queue.push({ kind: 'mat', id: outMatId });
    }
  }

  return { reachableMaterials, reachableOps };
}

function sortOperationsTopologically(
  graphData: GraphRecords,
  reachableOps: Set<string>,
  reachableMaterials: Set<string>,
  matToOps: Map<string, Set<string>>,
  opToMats: Map<string, Set<string>>,
): string[] {
  const indegree = new Map<string, number>();
  const opDeps = new Map<string, Set<string>>();

  for (const opId of reachableOps) {
    indegree.set(opId, 0);
    opDeps.set(opId, new Set<string>());
  }

  for (const opId of reachableOps) {
    for (const outMatId of opToMats.get(opId) ?? []) {
      if (!reachableMaterials.has(outMatId)) continue;
      for (const depOp of matToOps.get(outMatId) ?? []) {
        if (!reachableOps.has(depOp) || depOp === opId) continue;
        const deps = opDeps.get(opId)!;
        if (deps.has(depOp)) continue;
        deps.add(depOp);
        indegree.set(depOp, (indegree.get(depOp) ?? 0) + 1);
      }
    }
  }

  const queue: string[] = [];
  for (const [opId, deg] of indegree.entries()) {
    if (deg === 0) queue.push(opId);
  }

  queue.sort((a, b) => {
    const ao = graphData.operationNodes[a]?.creationOrder ?? Number.MAX_SAFE_INTEGER;
    const bo = graphData.operationNodes[b]?.creationOrder ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    return a.localeCompare(b);
  });

  const sorted: string[] = [];
  while (queue.length > 0) {
    const opId = queue.shift()!;
    sorted.push(opId);

    for (const outMatId of opToMats.get(opId) ?? []) {
      if (!reachableMaterials.has(outMatId)) continue;
      for (const depOp of matToOps.get(outMatId) ?? []) {
        if (!reachableOps.has(depOp) || depOp === opId) continue;
        const next = (indegree.get(depOp) ?? 0) - 1;
        indegree.set(depOp, next);
        if (next === 0) {
          queue.push(depOp);
          queue.sort((a, b) => {
            const ao = graphData.operationNodes[a]?.creationOrder ?? Number.MAX_SAFE_INTEGER;
            const bo = graphData.operationNodes[b]?.creationOrder ?? Number.MAX_SAFE_INTEGER;
            if (ao !== bo) return ao - bo;
            return a.localeCompare(b);
          });
        }
      }
    }
  }

  if (sorted.length === reachableOps.size) {
    return sorted;
  }

  const fallback = Array.from(reachableOps);
  fallback.sort((a, b) => {
    const ao = graphData.operationNodes[a]?.creationOrder ?? Number.MAX_SAFE_INTEGER;
    const bo = graphData.operationNodes[b]?.creationOrder ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    return a.localeCompare(b);
  });
  return fallback;
}

function copyIdentity(
  graphData: GraphRecords,
  targetMaterialId: string,
  sourceMaterialId: string,
  changedMaterialIds: Set<string>,
): void {
  const target = graphData.materialNodes[targetMaterialId];
  const source = graphData.materialNodes[sourceMaterialId];
  if (!target || !source) return;

  const sourceContainer = source.container;
  const targetContainer = target.container;
  const updatedContainer = {
    ...sourceContainer,
    id: targetContainer?.id ?? sourceContainer?.id ?? '',
  };

  graphData.materialNodes[targetMaterialId] = {
    ...target,
    name: source.name,
    container: updatedContainer,
  };
  changedMaterialIds.add(targetMaterialId);
}

function applyQuantity(
  graphData: GraphRecords,
  targetMaterialId: string,
  quantity: Quantity | null,
  changedMaterialIds: Set<string>,
): void {
  const target = graphData.materialNodes[targetMaterialId];
  if (!target) return;

  graphData.materialNodes[targetMaterialId] = {
    ...target,
    quantity: quantity
      ? {
          ...quantity,
          value: {
            ...quantity.value,
          },
        }
      : null,
  };
  changedMaterialIds.add(targetMaterialId);
}

function firstOperationInput(graphData: GraphRecords, operationId: string): MaterialNodeSpec | null {
  const inputEdge = Object.values(graphData.flowEdges).find(
    (edge) => edge.targetId === operationId && edge.edgeKind !== 'graphsLink' && graphData.materialNodes[edge.sourceId],
  );
  if (!inputEdge) return null;
  return graphData.materialNodes[inputEdge.sourceId] ?? null;
}

function resolveTransferAmounts(
  source: MaterialNodeSpec,
  row: TransferRow,
  transferEntireVolume: boolean,
): { transferredInSourceUnits: number; transferredInDestUnits: number } {
  const sourceUnit = source.quantity?.value?.units;
  if (!sourceUnit) {
    return { transferredInSourceUnits: 0, transferredInDestUnits: 0 };
  }

  const requestedAmount = transferEntireVolume
    ? Math.max(0, toFiniteNumber(source.quantity?.value?.amount, 0))
    : Math.max(0, toFiniteNumber(row.quantity?.value?.amount, 0));
  const requestedUnit = (row.quantity?.value?.units ?? sourceUnit) as Unit;

  const sourceAmount = Math.max(0, toFiniteNumber(source.quantity?.value?.amount, 0));
  const requestInSource = convertToUnit(requestedAmount, requestedUnit, sourceUnit);
  const transferredInSourceUnits = transferEntireVolume
    ? sourceAmount
    : Math.min(sourceAmount, Math.max(0, requestInSource));

  return {
    transferredInSourceUnits,
    transferredInDestUnits: transferredInSourceUnits,
  };
}

function recalcComplex(
  graphData: GraphRecords,
  operation: OperationNodeSpec,
  flags: PropagateFlags,
  changedMaterialIds: Set<string>,
  oldGraphData?: GraphRecords,
): void {
  const outputEdges = Object.values(graphData.flowEdges).filter(
    (edge) => edge.sourceId === operation.id && edge.edgeKind !== 'graphsLink' && graphData.materialNodes[edge.targetId],
  );

  const ioMappings = operation.complexOptions?.ioMapping ?? [];
  const oldOperation = oldGraphData?.operationNodes[operation.id!];
  const oldIoMappings = oldOperation?.complexOptions?.ioMapping ?? ioMappings;
  const outputIdsInMapping = new Set(ioMappings.map((m) => m.outputMaterialId));

  const inputEdges = Object.values(graphData.flowEdges).filter(
    (edge) => edge.targetId === operation.id && edge.edgeKind !== 'graphsLink' && graphData.materialNodes[edge.sourceId],
  );

  // Identify reminder nodes (outputs not in mapping) in order
  const reminderNodeIds: string[] = [];
  for (const outEdge of outputEdges) {
    if (!outputIdsInMapping.has(outEdge.targetId)) {
      reminderNodeIds.push(outEdge.targetId);
    }
  }

  // Map inputs to reminder nodes by order
  const inputToReminder = new Map<string, string>();
  const inputIds = inputEdges.map((e) => e.sourceId);
  for (let i = 0; i < inputIds.length && i < reminderNodeIds.length; i++) {
    inputToReminder.set(inputIds[i], reminderNodeIds[i]);
  }

  if (flags.quantity) {
    // Calculate old and new contributions for intelligent recalculation
    const oldContributionByOutput = new Map<string, number>();
    const newContributionByOutput = new Map<string, number>();
    const remainderByInput = new Map<string, number>();

    // Calculate BOTH old (if available) and new contributions
    for (const inEdge of inputEdges) {
      const newInputMaterial = graphData.materialNodes[inEdge.sourceId];
      const oldInputMaterial = oldGraphData?.materialNodes[inEdge.sourceId];
      
      const newInputAmount = Math.max(0, toFiniteNumber(newInputMaterial?.quantity?.value?.amount, 0));
      const oldInputAmount = oldGraphData ? Math.max(0, toFiniteNumber(oldInputMaterial?.quantity?.value?.amount, 0)) : newInputAmount;
      
      const mappings = ioMappings.filter((m) => m.inputMaterialId === inEdge.sourceId);
      const oldMappings = oldIoMappings.filter((m) => m.inputMaterialId === inEdge.sourceId);
      let newTransferred = 0;
      let oldTransferred = 0;

      for (const mapping of mappings) {
        const fraction = Number(mapping.fraction) || 0;
        const newContribution = newInputAmount * Math.max(0, fraction);
        const oldFraction = Number(
          oldMappings.find((m) => m.outputMaterialId === mapping.outputMaterialId)?.fraction ?? 0,
        ) || 0;
        const oldContribution = oldInputAmount * Math.max(0, oldFraction);
        
        newContributionByOutput.set(
          mapping.outputMaterialId,
          (newContributionByOutput.get(mapping.outputMaterialId) ?? 0) + newContribution,
        );
        oldContributionByOutput.set(
          mapping.outputMaterialId,
          (oldContributionByOutput.get(mapping.outputMaterialId) ?? 0) + oldContribution,
        );
        
        newTransferred += newContribution;
        oldTransferred += oldContribution;
      }

      // Include outputs that existed in the old mapping but were removed in the
      // new mapping so base-quantity reconstruction remains accurate.
      for (const oldMapping of oldMappings) {
        if (mappings.some((m) => m.outputMaterialId === oldMapping.outputMaterialId)) {
          continue;
        }
        const oldFraction = Number(oldMapping.fraction) || 0;
        const oldContribution = oldInputAmount * Math.max(0, oldFraction);
        oldContributionByOutput.set(
          oldMapping.outputMaterialId,
          (oldContributionByOutput.get(oldMapping.outputMaterialId) ?? 0) + oldContribution,
        );
        oldTransferred += oldContribution;
      }

      const newRemainder = Math.max(0, newInputAmount - newTransferred);
      remainderByInput.set(inEdge.sourceId, newRemainder);
    }

    // Apply to mapped outputs with intelligent recalculation
    for (const outEdge of outputEdges) {
      const outMat = graphData.materialNodes[outEdge.targetId];
      if (!outMat) continue;

      if (outputIdsInMapping.has(outEdge.targetId)) {
        // Mapped output (product) - use intelligent recalculation if we have old data
        const newContribution = newContributionByOutput.get(outEdge.targetId) ?? 0;
        
        if (oldGraphData) {
          // Intelligent recalculation: preserve base quantity
          const oldContribution = oldContributionByOutput.get(outEdge.targetId) ?? 0;
          const oldOutputMat = oldGraphData.materialNodes[outEdge.targetId];
          const oldOutputAmount = Math.max(0, toFiniteNumber(oldOutputMat?.quantity?.value?.amount, 0));
          
          // Base quantity = old output - old contributions
          const baseQuantity = Math.max(0, oldOutputAmount - oldContribution);
          // New output = base + new contributions
          const newOutputAmount = baseQuantity + newContribution;
          
          const unit = outMat.quantity?.value?.units
            ?? firstOperationInput(graphData, operation.id!)?.quantity?.value?.units
            ?? 'mL';
          const quantityType = outMat.quantity?.type
            ?? firstOperationInput(graphData, operation.id!)?.quantity?.type
            ?? 'Volume';
          
          applyQuantity(
            graphData,
            outEdge.targetId,
            {
              type: quantityType,
              value: {
                amount: newOutputAmount,
                units: unit,
              },
            },
            changedMaterialIds,
          );
        } else {
          // Fallback: simple recalculation (fresh calculation)
          const unit = outMat.quantity?.value?.units
            ?? firstOperationInput(graphData, operation.id!)?.quantity?.value?.units
            ?? 'mL';
          const quantityType = outMat.quantity?.type
            ?? firstOperationInput(graphData, operation.id!)?.quantity?.type
            ?? 'Volume';
          
          applyQuantity(
            graphData,
            outEdge.targetId,
            {
              type: quantityType,
              value: {
                amount: newContribution,
                units: unit,
              },
            },
            changedMaterialIds,
          );
        }
      } else {
        // Reminder node - find corresponding input
        let inputId: string | null = null;
        for (const [iId, rId] of inputToReminder) {
          if (rId === outEdge.targetId) {
            inputId = iId;
            break;
          }
        }

        if (inputId) {
          const inMat = graphData.materialNodes[inputId];
          const amount = remainderByInput.get(inputId) ?? 0;
          const quantity: Quantity | null = inMat?.quantity
            ? {
                type: inMat.quantity.type,
                value: {
                  amount,
                  units: inMat.quantity.value.units,
                },
              }
            : null;
          applyQuantity(graphData, outEdge.targetId, quantity, changedMaterialIds);
        }
      }
    }
  }

  // Update identity for reminder nodes
  if (flags.identity) {
    for (const [inputId, reminderNodeId] of inputToReminder) {
      copyIdentity(graphData, reminderNodeId, inputId, changedMaterialIds);
    }
  }

  // Update constituents for reminder nodes
  if (flags.constituents) {
    for (const [inputId, reminderNodeId] of inputToReminder) {
      const inputConstituents = graphData.materialNodes[inputId]?.constituents ?? [];
      const current = graphData.materialNodes[reminderNodeId];
      graphData.materialNodes[reminderNodeId] = {
        ...current,
        constituents: inputConstituents.map((c) => ({ ...c })),
      };
      changedMaterialIds.add(reminderNodeId);
    }
  }
}

export function propagateDownstream(
  graphData: GraphRecords,
  startMaterialId: string,
  options?: PropagateOptions,
): { changedMaterialIds: Set<string> } {
  const flags = normalizeFlags(options?.fields);
  const oldGraphData = options?.oldGraphData;
  const changedMaterialIds = new Set<string>();
  const startNode = graphData.materialNodes[startMaterialId];
  if (!startNode) {
    return { changedMaterialIds };
  }

  const { matToOps, opToMats } = buildAdjacency(graphData);
  const { reachableMaterials, reachableOps } = collectReachable(
    graphData,
    startMaterialId,
    matToOps,
    opToMats,
  );

  const orderedOps = sortOperationsTopologically(
    graphData,
    reachableOps,
    reachableMaterials,
    matToOps,
    opToMats,
  );

  for (const opId of orderedOps) {
    const operation = graphData.operationNodes[opId];
    if (!operation) continue;

    const outputEdges = Object.values(graphData.flowEdges).filter(
      (edge) => edge.sourceId === opId && edge.edgeKind !== 'graphsLink' && graphData.materialNodes[edge.targetId],
    );
    if (outputEdges.length === 0) continue;

    if (operation.archetype === 'TRANSFER') {
      const row = operation.transferRow;
      if (!row) continue;

      const sourceMaterialId = row.sourceMaterialId;
      const destinationMaterialId = row.destinationMaterialId;
      if (!sourceMaterialId || !destinationMaterialId) continue;

      const sourceMaterial = graphData.materialNodes[sourceMaterialId];
      const destinationMaterial = graphData.materialNodes[destinationMaterialId] ?? null;
      if (!sourceMaterial) continue;

      const transferEntireVolume = Boolean(operation.transferOptions?.transferEntireVolume);
      
      // Always preserve the originally transferred amount from the transfer row
      // Even if "transferEntireVolume" was used, the row.quantity stores what was transferred
      const transferredAmountValue = toFiniteNumber(row.quantity?.value?.amount, 0);
      const transferredUnit = row.quantity?.value?.units;
      
      const formRow = transferRowToFormState(row, { transferEntireVolume });

      const sourcePostId = row.sourcePostMaterialId;
      if (sourcePostId && graphData.materialNodes[sourcePostId]) {
        if (flags.identity) {
          copyIdentity(graphData, sourcePostId, sourceMaterialId, changedMaterialIds);
        }
        if (flags.quantity) {
          let qty: Quantity | null;
          
          // If we have a fixed transfer amount, calculate remainder as: source - transferred
          if (transferredAmountValue !== null && transferredAmountValue > 0) {
            const sourceUnit = sourceMaterial.quantity?.value?.units;
            if (sourceUnit) {
              const sourceAmount = toFiniteNumber(sourceMaterial.quantity?.value?.amount, 0);
              const transferred = convertToUnit(
                transferredAmountValue,
                row.quantity?.value?.units ?? sourceUnit,
                sourceUnit,
              );
              const remainder = Math.max(0, sourceAmount - transferred);
              qty = {
                value: { amount: remainder, units: sourceUnit },
                type: sourceMaterial.quantity?.type ?? 'Volume',
              };
            } else {
              qty = null;
            }
          } else {
            qty = computeSourcePostQuantity(sourceMaterial, formRow);
          }
          
          applyQuantity(graphData, sourcePostId, qty, changedMaterialIds);
        }
        if (flags.constituents) {
          const srcConstituents = sourceMaterial.constituents ?? [];
          const current = graphData.materialNodes[sourcePostId];
          graphData.materialNodes[sourcePostId] = {
            ...current,
            constituents: srcConstituents.map((c) => ({ ...c })),
          };
          changedMaterialIds.add(sourcePostId);
        }
      }

      const destinationPostId = row.destinationPostMaterialId;
      if (destinationPostId && graphData.materialNodes[destinationPostId]) {
        if (flags.identity && destinationMaterial) {
          copyIdentity(graphData, destinationPostId, destinationMaterialId, changedMaterialIds);
        }

        if (flags.quantity) {
          let mergedQty: Quantity | null;
          
          // If we have a fixed transfer amount, calculate merged as: dest + transferred
          if (transferredAmountValue !== null && transferredAmountValue > 0 && destinationMaterial?.quantity) {
            const destUnit = destinationMaterial.quantity.value.units;
            const destAmount = toFiniteNumber(destinationMaterial.quantity.value.amount, 0);
            const transferred = convertToUnit(
              transferredAmountValue,
              row.quantity?.value?.units ?? destUnit,
              destUnit,
            );
            mergedQty = {
              value: {
                amount: Math.max(0, destAmount + transferred),
                units: destUnit,
              },
              type: destinationMaterial.quantity.type,
            };
          } else if (destinationMaterial) {
            mergedQty = computeMergedQuantity(destinationMaterial.quantity, formRow);
          } else {
            mergedQty = computeMergedQuantity(null, formRow);
          }

          applyQuantity(graphData, destinationPostId, mergedQty, changedMaterialIds);
        }

        if (flags.constituents) {
          const srcConstituents = sourceMaterial.constituents ?? [];
          const destConstituents = destinationMaterial?.constituents ?? [];
          const merged = mergeConstituents(destConstituents, srcConstituents);
          const current = graphData.materialNodes[destinationPostId];
          graphData.materialNodes[destinationPostId] = {
            ...current,
            constituents: merged,
          };
          changedMaterialIds.add(destinationPostId);
        }
      }

      continue;
    }

    if (operation.archetype === 'COMPLEX') {
      recalcComplex(graphData, operation, flags, changedMaterialIds, oldGraphData);
      continue;
    }

    const input = firstOperationInput(graphData, opId);
    if (!input) continue;

    const yieldFactor = resolveYieldFactor(operation);

    for (const outEdge of outputEdges) {
      if (options?.skipIds?.has(outEdge.targetId)) continue;

      if (flags.identity) {
        copyIdentity(graphData, outEdge.targetId, input.id!, changedMaterialIds);
      }

      if (flags.constituents) {
        const out = graphData.materialNodes[outEdge.targetId];
        // For TRANSFORM/MEASURE: inherit input's constituents
        // If input is a root with no constituents, create one for it with its current name
        const inputConstituents = (input.constituents && input.constituents.length > 0)
          ? input.constituents.map((c) => ({ ...c }))  // Copy and preserve existing constituent lineage
          : [{ name: input.name, weight: 1 }];  // Root material: use its current name
        
        graphData.materialNodes[outEdge.targetId] = {
          ...out,
          constituents: inputConstituents,
        };
        changedMaterialIds.add(outEdge.targetId);
      }

      if (flags.quantity) {
        const quantity = input.quantity
          ? {
              ...input.quantity,
              value: {
                ...input.quantity.value,
                amount: toFiniteNumber(input.quantity.value.amount, 0) * yieldFactor,
              },
            }
          : null;
        applyQuantity(graphData, outEdge.targetId, quantity, changedMaterialIds);
      }
    }
  }

  changedMaterialIds.delete(startMaterialId);
  return { changedMaterialIds };
}

export { resolveYieldFactor };
